import React, { createContext, useContext, useState } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [totalPrice, setTotalPrice] = useState(
    localStorage.getItem('cartTotalValue') == null
      ? 0
      : parseFloat(localStorage.getItem('cartTotalValue'))
  );

  const [itemQuantities, setItemQuantities] = useState(
    localStorage.getItem('cartQuantityValue') == null
      ? {}
      : JSON.parse(localStorage.getItem('cartQuantityValue'))
  );

  const updateTotalPrice = (priceChange) => {
    const numericChange = parseFloat(priceChange);
    if (!isNaN(numericChange)) {
      setTotalPrice((prevTotal) => prevTotal + numericChange);
    } else {
      console.error('Invalid numeric value:', priceChange);
    }
  };

  const resetTotalPrice = () => {
    setTotalPrice(0);
  };

  // Helper function to create composite key
  const getCartKey = (productId, variantId) => {
    return `${productId}_${variantId}`;
  };

  const updateQuantity = (productId, newQuantity, price, offerPrice, variant) => {
    const finalPrice = offerPrice || price;
    const cartKey = getCartKey(productId, variant._id);

    setItemQuantities((prevQuantities) => {
      const updated = { ...prevQuantities };
      
      if (newQuantity === 0) {
        // Remove the item if quantity is 0
        delete updated[cartKey];
      } else {
        updated[cartKey] = {
          productId: productId,
          quantity: newQuantity,
          price: finalPrice,
          variant: variant
        };
      }
      
      return updated;
    });

    // Calculate new total
    let total = 0;
    const cartItems = { ...itemQuantities };
    
    if (newQuantity === 0) {
      delete cartItems[cartKey];
    } else {
      cartItems[cartKey] = {
        productId: productId,
        quantity: newQuantity,
        price: finalPrice,
        variant: variant
      };
    }

    Object.keys(cartItems).forEach((key) => {
      total += cartItems[key]?.quantity * parseFloat(cartItems[key]?.price);
    });

    setTotalPrice(total.toFixed(2));
    localStorage.setItem("cartQuantityValue", JSON.stringify(cartItems));
    localStorage.setItem("cartTotalValue", total.toFixed(2));
  };

  // Helper function to get quantity for a specific product-variant combination
  const getQuantityForVariant = (productId, variantId) => {
    const cartKey = getCartKey(productId, variantId);
    return itemQuantities[cartKey]?.quantity || 0;
  };

  // Helper function to get all variants of a product in cart
  const getProductVariantsInCart = (productId) => {
    return Object.keys(itemQuantities)
      .filter(key => itemQuantities[key]?.productId === productId)
      .map(key => itemQuantities[key]);
  };

  return (
    <CartContext.Provider
      value={{
        totalPrice,
        setTotalPrice,
        updateTotalPrice,
        resetTotalPrice,
        itemQuantities,
        setItemQuantities,
        updateQuantity,
        getCartKey,
        getQuantityForVariant,
        getProductVariantsInCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};